import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PiCaretLeft, PiPlus, PiTrash } from 'react-icons/pi'
import {
  primaryPillButtonClass,
  secondaryPillButtonClass,
} from 'components/FbUI/secondaryPillButton'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'
import { DashboardGrid } from './components/DashboardGrid'
import { LibraryRow, LibraryTable } from './components/LibraryTable'
import { SaveDialog, SaveResult } from './components/SaveDialog'
import { DashboardTileCard, TileViewTabs } from './components/DashboardTile'
import { defaultReportIcon, REPORT_ICONS } from './components/reportIcons'
import { ReportView } from './components/ViewSwitcher'
import {
  DashboardTile,
  defaultTileHeight,
  defaultTileSize,
  getDashboard,
  HEIGHT_TO_H,
  packTileLayout,
  SIZE_TO_W,
  TileLayout,
} from './dashboards'
import { REPORTING_PAGE_SHELL } from './page-shell'
import {
  getSavedDashboard,
  saveDashboard,
  updateSavedDashboard,
} from './saved-dashboards'
import { listSavedViews } from './saved-views'
import { TEMPLATES } from './templates'
import { ViewConfig } from './view-config'

/**
 * Build-your-own *dashboard*: a composition tool, not a report builder. You add
 * existing reports (the user's saved views + the shipped First Bite templates)
 * as tiles, drag to arrange them, then save — the result renders through the
 * same DashboardPage as the shipped dashboards.
 *
 * Tiles are tracked with a per-instance `uid` (not array index) so drag-reorder
 * and removal stay stable, and so the same report can appear more than once.
 */

type BuilderTileItem = { uid: string; tile: DashboardTile }

const makeUid = (): string =>
  `tile-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

/**
 * Header controls for a builder tile: the per-tile view picker + a remove
 * button. The `rgl-no-drag` class opts these out of react-grid-layout's drag (so
 * clicking a control never starts a tile move); width/height are no longer
 * pickers — the tile is resized by dragging its grid handles.
 */
function BuilderTileControls({
  tile,
  onSetView,
  onRemove,
}: {
  tile: DashboardTile
  onSetView: (view: ReportView) => void
  onRemove: () => void
}) {
  return (
    <div
      className="rgl-no-drag flex items-center gap-1.5"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <TileViewTabs tile={tile} onChange={onSetView} />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove widget"
        className="flex size-7 items-center justify-center rounded-md text-fg-secondary transition-colors hover:bg-surface-hover hover:text-error"
      >
        <PiTrash size={16} />
      </button>
    </div>
  )
}

/**
 * Quick-add menu for the actions row — a persistent "Add widget" button (the
 * palette below scrolls away as widgets accumulate). Adding doesn't close the
 * menu, so several widgets can be added in a row.
 */
function AddWidgetMenu({
  savedViews,
  onAdd,
}: {
  savedViews: ViewConfig[]
  onAdd: (config: ViewConfig) => void
}) {
  const row = (config: ViewConfig) => {
    const Icon = REPORT_ICONS[config.icon ?? defaultReportIcon(config, 'table')]
    return (
      <button
        key={config.id}
        type="button"
        onClick={() => onAdd(config)}
        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-fg hover:bg-surface-hover"
      >
        <Icon size={16} className="shrink-0 text-brand-active" />
        <span className="flex-1 truncate">{config.name}</span>
        <PiPlus size={14} className="shrink-0 text-fg-secondary" />
      </button>
    )
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={secondaryPillButtonClass}>
          <PiPlus size={16} /> Add widget
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-80 w-64 overflow-y-auto p-1">
        {savedViews.length > 0 && (
          <>
            <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-fg-secondary">
              Your reports
            </div>
            {savedViews.map(row)}
          </>
        )}
        <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-fg-secondary">
          First Bite reports
        </div>
        {TEMPLATES.map(row)}
      </PopoverContent>
    </Popover>
  )
}

function DashboardBuilderInner({ seedId }: { seedId?: string }) {
  const navigate = useNavigate()
  // Editing an existing dashboard seeds the canvas from its tiles (shipped or
  // saved). Saving always creates a NEW dashboard ("save as new").
  const seed = seedId
    ? (getDashboard(seedId) ?? getSavedDashboard(seedId))
    : undefined
  const editing = !!seed
  const [name, setName] = useState(seed?.name ?? '')
  const [description, setDescription] = useState(seed?.description ?? '')
  const [items, setItems] = useState<BuilderTileItem[]>(() => {
    if (!seed) return []
    // Seed every tile with a concrete grid layout so drag/resize edits have a
    // base; shipped dashboards (authored without one) get it from the packer.
    const packed = packTileLayout(seed.tiles)
    return seed.tiles.map((tile, i) => ({
      uid: makeUid(),
      tile: { ...tile, layout: tile.layout ?? packed[i] },
    }))
  })
  // savedViews is read once on mount — fine for the prototype (no live sync).
  const [savedViews] = useState(listSavedViews)

  const addTile = (config: ViewConfig) =>
    setItems((prev) => {
      const size = defaultTileSize(config)
      const height = defaultTileHeight(config)
      // Drop the new tile on a fresh row below everything already placed.
      const y = prev.reduce(
        (max, i) =>
          Math.max(max, (i.tile.layout?.y ?? 0) + (i.tile.layout?.h ?? 0)),
        0
      )
      const layout: TileLayout = {
        x: 0,
        y,
        w: SIZE_TO_W[size],
        h: HEIGHT_TO_H[height],
      }
      return [
        ...prev,
        { uid: makeUid(), tile: { config, size, height, layout } },
      ]
    })

  // Picker rows: saved views (Mine) + shipped templates (Pre-built). Goals aren't
  // renderable tiles, so they're excluded. Each row's Add action drops a tile.
  const pickerRows: LibraryRow[] = [
    ...savedViews.map((v) => ({
      kind: 'view' as const,
      source: 'mine' as const,
      view: v,
      onAdd: () => addTile(v),
    })),
    ...TEMPLATES.map((t) => ({
      kind: 'view' as const,
      source: 'prebuilt' as const,
      view: t,
      onAdd: () => addTile(t),
    })),
  ]

  const removeTile = (uid: string) =>
    setItems((prev) => prev.filter((i) => i.uid !== uid))

  // Per-tile view override — how the report shows on this dashboard only.
  const setView = (uid: string, view: ReportView) =>
    setItems((prev) =>
      prev.map((i) => (i.uid === uid ? { ...i, tile: { ...i.tile, view } } : i))
    )

  // react-grid-layout reports the new placement of every tile after a drag or
  // resize; fold it back onto each item (matched by uid) so saving keeps it.
  const applyLayout = (next: Record<string, TileLayout>) =>
    setItems((prev) =>
      prev.map((i) =>
        next[i.uid] ? { ...i, tile: { ...i.tile, layout: next[i.uid] } } : i
      )
    )

  const [saveOpen, setSaveOpen] = useState(false)
  const canSave = items.length > 0
  // Only a user-saved dashboard can be updated in place; shipped ones (and new
  // ones) can only be saved as new.
  const canUpdate = !!(seedId && getSavedDashboard(seedId))
  const tiles = () => items.map((i) => i.tile)

  const handleSaveNew = ({ name: saveName, description }: SaveResult) => {
    if (!canSave) return
    const saved = saveDashboard(
      saveName,
      tiles(),
      description || 'Custom dashboard.'
    )
    navigate(`/reporting/dashboards/${saved.id}`)
  }
  const handleOverwrite = ({ name: saveName, description }: SaveResult) => {
    if (!canSave || !seedId) return
    const updated = updateSavedDashboard(
      seedId,
      saveName,
      tiles(),
      description || undefined
    )
    if (updated) navigate(`/reporting/dashboards/${updated.id}`)
  }

  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-6')}>
      <div>
        <Link
          to="/reporting"
          className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
        >
          <PiCaretLeft size={14} /> Reports
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Untitled dashboard"
              aria-label="Dashboard name"
              className="w-full bg-transparent text-2xl font-bold text-fg outline-none placeholder:text-fg-muted"
            />
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description (optional)"
              aria-label="Dashboard description"
              className="w-full bg-transparent text-sm text-fg-secondary outline-none placeholder:text-fg-muted"
            />
            <p className="text-sm text-fg-secondary">
              {editing
                ? 'Drag to move, pull an edge to resize, then save it as a new dashboard.'
                : 'Add your reports as widgets, then drag and resize them on the grid.'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <AddWidgetMenu savedViews={savedViews} onAdd={addTile} />
            <Link to="/reporting" className={secondaryPillButtonClass}>
              Cancel
            </Link>
            <button
              type="button"
              onClick={() => setSaveOpen(true)}
              disabled={!canSave}
              className={primaryPillButtonClass}
            >
              {canUpdate ? 'Save…' : 'Save dashboard…'}
            </button>
          </div>
        </div>
      </div>

      {/* Canvas — the dashboard being composed. */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line py-16 text-center">
          <p className="text-sm font-medium text-fg">No widgets yet</p>
          <p className="text-sm text-fg-secondary">
            Use “Add widget” above, or pick a report below, to start building.
          </p>
        </div>
      ) : (
        <DashboardGrid
          editable
          items={items.map((i) => ({ id: i.uid, tile: i.tile }))}
          onLayoutChange={applyLayout}
          renderItem={({ id, tile }) => (
            <DashboardTileCard
              tile={tile}
              linkTo={null}
              action={
                <BuilderTileControls
                  tile={tile}
                  onSetView={(view) => setView(id, view)}
                  onRemove={() => removeTile(id)}
                />
              }
            />
          )}
        />
      )}

      {/* Widget palette — same source-tagged report table as the library, in
          picker mode (each row's action adds it as a tile). */}
      <section className="flex flex-col gap-4 rounded-lg border border-line bg-surface-sunken p-4">
        <div>
          <h2 className="text-sm font-semibold text-fg">Add a widget</h2>
          <p className="text-sm text-fg-secondary">
            Pick from your saved reports and the First Bite reports.
          </p>
        </div>
        <LibraryTable rows={pickerRows} picker filterable />
      </section>

      <SaveDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        kind="dashboard"
        defaultName={
          canUpdate ? name.trim() || 'Untitled dashboard' : name.trim()
        }
        defaultDescription={description}
        canOverwrite={canUpdate}
        onSaveNew={handleSaveNew}
        onOverwrite={handleOverwrite}
      />
    </div>
  )
}

export default function DashboardBuilderPage() {
  // `:dashboardId` present → editing an existing dashboard (seeded); absent →
  // a blank new dashboard. Keyed so state re-initializes when the id changes.
  const { dashboardId } = useParams()
  return (
    <DashboardBuilderInner key={dashboardId ?? 'new'} seedId={dashboardId} />
  )
}
